import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AuthService from "../Auth/AuthService";
import "./Header.css";

// Simple inline SVG icons — no emoji, no FontAwesome dependency for these
const IconChevron = ({ className }) => (
  <svg className={`nav-chevron ${className || ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const IconUser = () => (
  <svg className="profile-dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const IconLogout = () => (
  <svg className="profile-dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const Header = ({ userType }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProfileOpen, setProfileOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [isMobileOpen, setMobileOpen] = useState(false);
  const [mobileSubMenu, setMobileSubMenu] = useState(null);

  const profileRef = useRef(null);
  const desktopNavRef = useRef(null);

  const menuItems = (() => {
    switch (userType) {
      case "admin":
        return [
          { label: "Manage Organization", path: "/manage-organisation" },
          { label: "Settings", path: "/settings" },
          { label: "Reports", path: "/reports" },
        ];
      case "organisation":
        return [
          { label: "Manage Agents", path: "/manage-agents" },
          { label: "Chats", path: "/chat" },
          { label: "All Tickets", path: "/all-tickets" },
          { label: "Contacts", path: "/contacts" },
          {
            label: "Settings",
            submenu: [
              { label: "Get Code", path: "/chat-settings" },
              { label: "Knowledge Center", path: "/knowledge-center" },
              { label: "Subscription", path: "/subscriptions" },
              { label: "Chatbot Setup", path: "/chatbot-setup" },
            ]
          }
        ];
      case "agent":
        return [
          { label: "Chats", path: "/chat" },
          { label: "All Tickets", path: "/all-tickets" },
          { label: "Contacts", path: "/contacts" },
          {
            label: "Settings",
            submenu: [
              { label: "Subscription", path: "/subscriptions" },
            ]
          }
        ];
      default:
        return [];
    }
  })();

  const handleLogout = async () => {
    try {
      await AuthService.logout();
      localStorage.removeItem("user");
      localStorage.removeItem("authToken");
      navigate("/login");
    } catch (error) {
      console.log("Logout error!");
    }
  };

  const isActive = (item) => {
    if (item.path && location.pathname === item.path) return true;
    if (item.submenu) return item.submenu.some(s => location.pathname === s.path);
    return false;
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user?.name) return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    } catch {}
    return "U";
  };

  // Close profile dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
      if (desktopNavRef.current && !desktopNavRef.current.contains(e.target)) {
        setOpenSubMenu(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isMobileOpen]);

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileSubMenu(null);
  };

  return (
    <>
      <header className="app-header">
        <div className="header-inner">

          {/* Logo */}
          <div className="header-logo" onClick={() => navigate("/dashboard")}>
            <img src="/logo_Group.svg" alt="EzzDesk" />
          </div>

          {/* Desktop Navigation */}
          <nav className="header-nav" ref={desktopNavRef}>
            {menuItems.map((item, index) => (
              <div key={index} style={{ position: 'relative' }}>
                <button
                  className={`nav-link-item ${isActive(item) ? 'active' : ''}`}
                  onClick={() => {
                    if (item.submenu) {
                      setOpenSubMenu(openSubMenu === item.label ? null : item.label);
                    } else {
                      navigate(item.path);
                      setOpenSubMenu(null);
                    }
                  }}
                >
                  {item.label}
                  {item.submenu && <IconChevron className={openSubMenu === item.label ? 'open' : ''} />}
                </button>

                {item.submenu && openSubMenu === item.label && (
                  <div className="nav-dropdown">
                    {item.submenu.map((sub, si) => (
                      <button
                        key={si}
                        className="nav-dropdown-item"
                        onClick={() => {
                          navigate(sub.path);
                          setOpenSubMenu(null);
                        }}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right side */}
          <div className="header-right">
            {/* Profile button (desktop) */}
            {userType && (
              <div style={{ position: 'relative' }} ref={profileRef}>
                <button className="profile-btn" onClick={() => setProfileOpen(p => !p)}>
                  {getUserInitials()}
                </button>
                {isProfileOpen && (
                  <div className="profile-dropdown">
                    <button className="profile-dropdown-item" onClick={() => { navigate("/profile"); setProfileOpen(false); }}>
                      <IconUser /> Profile
                    </button>
                    <div className="profile-dropdown-divider" />
                    <button className="profile-dropdown-item danger" onClick={handleLogout}>
                      <IconLogout /> Logout
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Hamburger (mobile only) */}
            <button
              className={`hamburger-btn ${isMobileOpen ? 'open' : ''}`}
              onClick={() => setMobileOpen(o => !o)}
              aria-label="Toggle menu"
            >
              <span className="hamburger-line" />
              <span className="hamburger-line" />
              <span className="hamburger-line" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Overlay */}
      <div
        className={`mobile-overlay ${isMobileOpen ? 'visible' : ''}`}
        onClick={closeMobile}
      />

      {/* Mobile Drawer */}
      <div className={`mobile-drawer ${isMobileOpen ? 'open' : ''}`}>
        {/* Drawer header */}
        <div className="mobile-drawer-header">
          <div className="mobile-drawer-logo" onClick={() => { navigate("/dashboard"); closeMobile(); }} style={{ cursor: 'pointer' }}>
            <img src="/logo_Group.svg" alt="EzzDesk" />
          </div>
          <button className="mobile-drawer-close" onClick={closeMobile}>✕</button>
        </div>

        {/* Drawer nav */}
        <nav className="mobile-nav">
          {menuItems.map((item, index) => (
            <div key={index}>
              <button
                className={`mobile-nav-item ${isActive(item) ? 'active' : ''}`}
                onClick={() => {
                  if (item.submenu) {
                    setMobileSubMenu(mobileSubMenu === item.label ? null : item.label);
                  } else {
                    navigate(item.path);
                    closeMobile();
                  }
                }}
              >
                {item.label}
                {item.submenu && <IconChevron className={mobileSubMenu === item.label ? 'open' : ''} />}
              </button>

              {item.submenu && mobileSubMenu === item.label && (
                <div className="mobile-sub-nav">
                  {item.submenu.map((sub, si) => (
                    <button
                      key={si}
                      className="mobile-sub-item"
                      onClick={() => { navigate(sub.path); closeMobile(); }}
                    >
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Drawer footer */}
        {userType && (
          <div className="mobile-drawer-footer">
            <button className="mobile-footer-btn" onClick={() => { navigate("/profile"); closeMobile(); }}>
              <IconUser /> Profile
            </button>
            <button className="mobile-footer-btn logout" onClick={handleLogout}>
              <IconLogout /> Logout
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Header;